// ============================================================================
// ONE POS klienti — Supabase bilan to'g'ridan-to'g'ri HTTP orqali ishlaydi.
//
//   signIn(email, parol)   -> sessiya (Auth)
//   rpc(nom, argumentlar)  -> /rest/v1/rpc/<nom>   (baza funksiyalari)
//   invoke(nom, body)      -> /functions/v1/<nom>  (Edge Function)
//
// Sessiya brauzer xotirasida saqlanadi va muddati tugashidan oldin
// avtomatik yangilanadi. Bir vaqtda faqat bitta yangilash so'rovi ketadi.
// ============================================================================

import { ApiError, toApiError } from './errors';

export interface SessionUser {
  id: string;
  email: string | null;
}

export interface Session {
  access_token: string;
  refresh_token: string;
  /** Unix vaqti, soniya */
  expires_at: number;
  user: SessionUser;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ClientConfig {
  url: string;
  /** Ochiq (publishable/anon) kalit — brauzerda turishi mumkin */
  key: string;
  /** Sessiya saqlanadigan kalit, masalan 'onepos.console.session' */
  storageKey: string;
  storage?: KeyValueStorage | null;
  fetch?: typeof fetch;
  /** Hozirgi vaqt, millisoniya (testlar uchun) */
  now?: () => number;
}

type Listener = (session: Session | null) => void;

/** Muddati tugashiga shuncha soniya qolganda token yangilanadi */
const REFRESH_MARGIN_S = 60;

function defaultStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** JWT ichidagi ma'lumot (imzo tekshirilmaydi — faqat ko'rsatish uchun) */
export function decodeJwt(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function parseSession(raw: unknown, nowS: number): Session | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.access_token !== 'string' || typeof r.refresh_token !== 'string') return null;
  const user = (r.user ?? {}) as Record<string, unknown>;
  const expiresAt =
    typeof r.expires_at === 'number'
      ? r.expires_at
      : typeof r.expires_in === 'number'
        ? nowS + r.expires_in
        : nowS + 3600;
  return {
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    expires_at: expiresAt,
    user: { id: String(user.id ?? ''), email: typeof user.email === 'string' ? user.email : null },
  };
}

export class OnePosClient {
  private readonly cfg: Required<Omit<ClientConfig, 'storage'>> & { storage: KeyValueStorage | null };
  private current: Session | null = null;
  private refreshing: Promise<Session | null> | null = null;
  private readonly listeners = new Set<Listener>();

  constructor(config: ClientConfig) {
    this.cfg = {
      url: config.url.replace(/\/+$/, ''),
      key: config.key,
      storageKey: config.storageKey,
      storage: config.storage === undefined ? defaultStorage() : config.storage,
      fetch: config.fetch ?? ((...args) => fetch(...args)),
      now: config.now ?? (() => Date.now()),
    };
    this.current = this.load();
  }

  // -------------------------------------------------------------------------
  // Sessiya
  // -------------------------------------------------------------------------
  get session(): Session | null {
    return this.current;
  }

  /** Tokendagi "op" bo'limi: {k, tid, bid, trm, mr, sm} */
  claims(): Record<string, unknown> | null {
    if (!this.current) return null;
    const payload = decodeJwt(this.current.access_token);
    const op = payload?.op;
    return op && typeof op === 'object' ? (op as Record<string, unknown>) : {};
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async signIn(email: string, password: string): Promise<Session> {
    const body = await this.authRequest('token?grant_type=password', { email: email.trim(), password });
    const session = parseSession(body, this.nowS());
    if (!session) throw new ApiError('SERVER_ERROR', 500, 'Sessiya kelmadi');
    this.setSession(session);
    return session;
  }

  async signOut(): Promise<void> {
    const token = this.current?.access_token;
    this.setSession(null);
    if (!token) return;
    try {
      await this.cfg.fetch(`${this.cfg.url}/auth/v1/logout`, {
        method: 'POST',
        headers: { apikey: this.cfg.key, Authorization: `Bearer ${token}` },
      });
    } catch {
      /* chiqish mahalliy ravishda bajarildi — server javobi muhim emas */
    }
  }

  /** Tokenni yangilash (masalan, restoran yoki yordam sessiyasi o'zgargach). Xato bo'lsa sessiya yopiladi. */
  refresh(): Promise<Session | null> {
    if (this.refreshing) return this.refreshing;
    const refreshToken = this.current?.refresh_token;
    if (!refreshToken) return Promise.resolve(null);
    this.refreshing = (async () => {
      try {
        const body = await this.authRequest('token?grant_type=refresh_token', { refresh_token: refreshToken });
        const session = parseSession(body, this.nowS());
        this.setSession(session);
        return session;
      } catch (e) {
        if (e instanceof ApiError && e.code !== 'NETWORK') this.setSession(null);
        throw e;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  /** Yaroqli kirish tokeni (kerak bo'lsa yangilab) yoki null */
  async accessToken(): Promise<string | null> {
    if (!this.current) return null;
    if (this.current.expires_at - this.nowS() <= REFRESH_MARGIN_S) {
      const s = await this.refresh();
      return s?.access_token ?? null;
    }
    return this.current.access_token;
  }

  // -------------------------------------------------------------------------
  // Chaqiruvlar
  // -------------------------------------------------------------------------
  /** Baza funksiyasi (RPC). Xato bo'lsa ApiError (kod + o'zbekcha matn). */
  rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
    return this.authed<T>(`${this.cfg.url}/rest/v1/rpc/${encodeURIComponent(name)}`, args);
  }

  /**
   * Jadvaldan o'qish (RLS: faqat o'z restorani). path — PostgREST so'rovi,
   * masalan "products?select=*&order=sort_order".
   */
  async select<T>(path: string): Promise<T[]> {
    return (await this.authed<T[]>(`${this.cfg.url}/rest/v1/${path}`, undefined, false, 'GET')) ?? [];
  }

  /** Edge Function. Javob {ok:false, error} bo'lsa — ApiError. */
  async invoke<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
    const res = await this.authed<T & { ok?: boolean; error?: string }>(
      `${this.cfg.url}/functions/v1/${encodeURIComponent(name)}`,
      body,
    );
    if (res && typeof res === 'object' && res.ok === false) throw toApiError(400, res);
    return res;
  }

  private async authed<T>(url: string, body: unknown, retried = false, method: 'GET' | 'POST' = 'POST'): Promise<T> {
    const token = await this.accessToken();
    const headers: Record<string, string> = { apikey: this.cfg.key, 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    let res: Response;
    try {
      res = await this.cfg.fetch(url, method === 'GET' ? { method, headers } : { method, headers, body: JSON.stringify(body) });
    } catch {
      throw new ApiError('NETWORK', 0);
    }
    const data = await readJson(res);
    if (res.status === 401 && token && !retried) {
      // Token server tomonda bekor bo'lgan bo'lishi mumkin — bir marta yangilab ko'ramiz
      const s = await this.refresh().catch(() => null);
      if (s) return this.authed<T>(url, body, true, method);
      throw new ApiError('SESSION_EXPIRED', 401);
    }
    if (!res.ok) throw toApiError(res.status, data);
    return data as T;
  }

  private async authRequest(path: string, body: unknown): Promise<unknown> {
    let res: Response;
    try {
      res = await this.cfg.fetch(`${this.cfg.url}/auth/v1/${path}`, {
        method: 'POST',
        headers: { apikey: this.cfg.key, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new ApiError('NETWORK', 0);
    }
    const data = await readJson(res);
    if (!res.ok) throw toApiError(res.status, data);
    return data;
  }

  // -------------------------------------------------------------------------
  // Saqlash
  // -------------------------------------------------------------------------
  private nowS(): number {
    return Math.floor(this.cfg.now() / 1000);
  }

  private setSession(session: Session | null): void {
    this.current = session;
    try {
      if (session) this.cfg.storage?.setItem(this.cfg.storageKey, JSON.stringify(session));
      else this.cfg.storage?.removeItem(this.cfg.storageKey);
    } catch {
      /* saqlab bo'lmadi — sessiya faqat shu sahifada qoladi */
    }
    for (const l of this.listeners) l(session);
  }

  private load(): Session | null {
    try {
      const raw = this.cfg.storage?.getItem(this.cfg.storageKey);
      return raw ? parseSession(JSON.parse(raw), this.nowS()) : null;
    } catch {
      return null;
    }
  }
}

async function readJson(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}
